import type { Project, PadContent } from '@/stores/sequencerStore'
import type { KitVoice, VoiceType } from '@/hooks/useAudioEngine'

/**
 * Resolved view of a single pad slot (index 0..15) — a discriminated
 * union representation that folds the two sources of truth into one:
 *
 *   1. `project.padDefs[padIndex]` (explicit user override), OR
 *   2. the kit's default voice at that index (if any).
 *
 * If neither source has content for the slot, `resolvePad` returns
 * `null` — the pad is empty and the caller should skip / render an
 * "Add" affordance.
 *
 * Callers read only from the fields relevant to their kind — the
 * scheduler, audio engine, and MIDI-in matcher all check
 * `resolved.kind` first. Keeping a single flat interface (vs. one
 * type per variant) means consumers that only care about common
 * fields — label / midiNote — don't need to switch on `kind`.
 */
export interface ResolvedPad {
  kind: 'synth' | 'sample' | 'midi' | 'granular' | 'instrument'
  /** Display name shown on the pad button. */
  label: string
  /** MIDI note this pad emits on MIDI-out AND matches on MIDI-in. */
  midiNote: number
  /** synth only — which procedural voice to synthesise */
  voice?: VoiceType
  /** sample / granular / sampler-instrument — ids for the
   *  AudioBuffer cache + S3 GET URL. */
  sampleId?: string
  s3Key?: string
  /** sample / sampler-instrument — when set, play only this
   *  named slice region. */
  sliceId?: string
  /** sampler-instrument — explicit slice region override (bypasses
   *  sliceId lookup on the sample). Seconds from sample start. */
  sliceStart?: number
  /** sampler-instrument — slice region length in seconds. */
  sliceLen?: number
  /** midi only — channel 1..16 */
  channel?: number
  /** sample / granular / instrument — per-pad gain in dBFS. */
  gainDb?: number
  /** instrument only — registry plugin id. */
  instrumentId?: string
  /** instrument only — plugin preset forwarded to createVoice. */
  preset?: Record<string, number | string>
  /**
   * Keys Mode only — additive pitch-semitones offset layered on
   * top of any step pitch lock when this pad fires. Computed as
   * `(scaleNote - focusedPadNaturalNote)` so every pad plays the
   * focused pad's sound at the chromatic scale position. Unset
   * when Keys Mode is off; undefined on the focused pad itself
   * (offset = 0).
   */
  keysModePitchSemitones?: number
  /**
   * sample only — how the pad responds to cell.length locks.
   * Forwarded from `PadContent.playback`; undefined → treated as
   * `'one-shot'` by the fire paths. See the sample-pad type in
   * `packages/shared/src/types/sequencer.ts` for rationale.
   */
  playback?: 'one-shot' | 'gated'
}

/**
 * Resolve the content at a single pad index. Always returns a
 * stable shape (or null) — callers don't need to know the
 * precedence rules.
 *
 * `kitVoices` is passed in (rather than derived from a `KitId`)
 * because callers often already have the current kit's voices
 * array in scope, and passing it explicitly makes this a pure
 * function with no module-level kit lookup.
 */
export function resolvePad(
  project: Project,
  kitVoices: ReadonlyArray<KitVoice | undefined>,
  padIndex: number,
): ResolvedPad | null {
  const override = project.padDefs?.[padIndex]
  if (override) return fromPadContent(override)

  const kitVoice = kitVoices[padIndex]
  if (kitVoice) {
    return {
      kind: 'synth',
      label: kitVoice.label,
      midiNote: kitVoice.midiNote,
      voice: kitVoice.type,
    }
  }

  return null
}

function fromPadContent(c: PadContent): ResolvedPad {
  switch (c.kind) {
    case 'synth':
      return {
        kind: 'synth',
        label: c.label ?? defaultLabelForVoice(c.voice),
        midiNote: c.midiNote,
        voice: c.voice,
      }
    case 'sample':
      return {
        kind: 'sample',
        label: c.name ?? 'Sample',
        midiNote: c.midiNote,
        sampleId: c.sampleId,
        s3Key: c.s3Key,
        sliceId: c.sliceId,
        gainDb: c.gainDb,
        playback: c.playback,
      }
    case 'midi':
      return {
        kind: 'midi',
        label: c.label,
        midiNote: c.midiNote,
        channel: c.channel,
      }
    case 'granular':
      return {
        kind: 'granular',
        label: c.name ?? 'Granular',
        midiNote: c.midiNote,
        sampleId: c.sampleId,
        s3Key: c.s3Key,
        gainDb: c.gainDb,
      }
    case 'instrument':
      return {
        kind: 'instrument',
        label: c.label ?? 'Instrument',
        midiNote: c.midiNote,
        instrumentId: c.instrumentId,
        preset: c.preset,
        gainDb: c.gainDb,
        sampleId: c.sampleId,
        s3Key: c.s3Key,
        sliceId: c.sliceId,
        sliceStart: c.sliceStart,
        sliceLen: c.sliceLen,
      }
  }
}

const VOICE_LABELS: Record<VoiceType, string> = {
  kick: 'Kick',
  snare: 'Snare',
  hatClosed: 'HH Closed',
  hatOpen: 'HH Open',
  clap: 'Clap',
  tom: 'Tom',
  ride: 'Ride',
  cowbell: 'Cowbell',
}

function defaultLabelForVoice(voice: VoiceType): string {
  return VOICE_LABELS[voice] ?? voice
}

/** All VoiceType values — used by pickers in PadEditor. */
export const VOICE_TYPES: readonly VoiceType[] = [
  'kick',
  'snare',
  'hatClosed',
  'hatOpen',
  'clap',
  'tom',
  'ride',
  'cowbell',
]

export function labelForVoice(voice: VoiceType): string {
  return defaultLabelForVoice(voice)
}

/**
 * Default MIDI note for a fresh pad at index `padIndex`. Pads lay
 * out chromatically starting at C3 (MIDI 48), so pad N defaults
 * to note `48 + N`. Clamped to the valid MIDI range because a
 * hypothetical future with > 80 pads would overflow. This helper
 * is used when the user adds a new synth / sample / MIDI pad to
 * a slot that doesn't already carry a kit default — it keeps the
 * pad→note mapping uniform across all three content kinds.
 */
export function defaultNoteForPad(padIndex: number): number {
  return Math.max(0, Math.min(127, 48 + padIndex))
}
